/**
 * bb-cart-demand.js — product Add-to-Cart demand telemetry.
 *
 * ANALYTICS MAY FAIL. SHOPPING MAY NOT.
 * Every line below is written on that assumption. Nothing here can reject, throw into,
 * delay, or alter a customer's cart operation.
 *
 * WHY THIS INTERCEPTS fetch INSTEAD OF LISTENING FOR THE THEME'S EVENT
 * -------------------------------------------------------------------
 * This theme has thirteen separate add-to-cart code paths and only three of them
 * publish PUB_SUB_EVENTS.cartUpdate. Subscribing to that event would capture a
 * minority of real adds and rank products by which template they happen to live in --
 * for a DEMAND signal, worse than collecting nothing. It would also over-count:
 * edit-cart.js publishes the same event for quantity changes, which are not adds.
 *
 * One layer down, every path but one funnels into fetch() at /cart/add. Observing that
 * is a single integration point covering twelve of thirteen paths.
 *
 * THE DECLARED GAP: "Buy Now" in section-mobile-sticky-atc.liquid submits a native
 * <form> and navigates away. No fetch, no response, nothing this document can observe.
 * It is reported as a known gap rather than faked with a click listener.
 *
 * WHAT IT SENDS
 * -------------
 * Product id, variant id, confirmed quantity, timestamp, surface. No customer id, no
 * session id, nothing personal. The question is "which products are wanted?", never
 * "what is this person like?".
 */

/* ===========================================================================
   PHASE 3 — CONSENT GATE, shared by both collectors in this file.

   WHY THIS EXISTS
   ---------------
   A Phase 3 audit drove five consent states through a real browser against the live
   storefront. Shopify's own privacy layer already gates the app pixels that DECLARE a
   privacy purpose -- Meta and Pinterest stop firing the moment marketing or
   sale_of_data is denied. Our own first-party beacons are not app pixels, so nothing
   gated them: they fired in every state, including with analytics explicitly denied.
   This closes that.

   A Phase 1 conclusion is also corrected here. It reported "no consent controls
   detected" because window.Shopify.customerPrivacy was absent. The API has to be
   LOADED first, with Shopify.loadFeatures -- its absence was evidence of nothing.
   Loaded properly, the API is present and works.

   WHY analyticsProcessingAllowed() IS THE WHOLE RULE
   -------------------------------------------------
   It already means both things we need, so no region logic of our own is required:
     - in a region with no consent requirement it returns TRUE with consent unset,
       which preserves legitimate analytics (measured: true, with
       currentVisitorConsent all empty and shouldShowBanner false);
     - in a region that requires consent it returns FALSE until consent is granted.

   DELIBERATELY NOT "if the API is unavailable, send anyway". If we cannot read the
   consent state we do not know which kind of region we are in, and guessing in the
   permissive direction is exactly the thing this phase exists to remove. Unknown
   therefore means QUEUE, and if it never resolves, DROP. That costs a rounding error
   in a signal this file already treats as droppable -- it has no retry for the same
   reason -- and it cannot leak a request a visitor did not permit.

   ANALYTICS, not marketing: these beacons measure product demand. Nothing here
   retargets, builds an audience, or leaves our own origin. The legal classification is
   still a lawyer's call and is flagged as such in the Phase 3 report; gating on the
   stricter of the plausible signals is the conservative default while that is settled.

   SHOPPING MAY NOT FAIL. Every entry point is wrapped, every failure resolves to
   "do not send", and nothing here is on the add-to-cart critical path: the cart
   collector consults this only inside send(), which already hangs off a branch that
   nothing awaits.
   =========================================================================== */
(function () {
  'use strict';

  if (window.__bbConsentGate) return;               /* install once */

  var MAX_QUEUE = 10;
  var LOAD_TIMEOUT_MS = 10000;

  var gate = {
    installed_at: new Date().toISOString(),
    api_loaded: false,
    load_error: null,
    /* 'unknown' until the API answers. Never initialised to a permissive value. */
    last_known: 'unknown',
    allowed_sends: 0,
    blocked_sends: 0,
    queued: 0,
    dropped_unresolved: 0,
    consent_events: 0
  };
  window.__bbConsentGate = gate;

  var queue = [];
  var resolvedOnce = false;
  var dropTimer = null;
  /* Set when the Customer Privacy API can never answer -- no loader at all. Then the
     honest state is not "wait and see" but "we will never know", so nothing is queued
     and no timer is armed: send() refuses immediately. */
  var unresolvable = false;

  function api() {
    try {
      return (window.Shopify && window.Shopify.customerPrivacy) || null;
    } catch (e) { return null; }
  }

  /* true | false | null(unknown). null is a real answer and never coerced. */
  function analyticsAllowed() {
    var cp = api();
    if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return null;
    try {
      var v = cp.analyticsProcessingAllowed();
      return v === true ? true : (v === false ? false : null);
    } catch (e) { return null; }
  }

  function runOne(fn) {
    try { fn(); } catch (e) { /* a telemetry send must never throw outward */ }
  }

  /* Nothing is pending once the answer is known, so the drop-timer is cleared rather
     than left to fire into a settled gate. */
  function clearDropTimer() {
    if (dropTimer === null) return;
    try { if (window.clearTimeout) window.clearTimeout(dropTimer); } catch (e) { /* fine */ }
    dropTimer = null;
  }

  function flush() {
    var verdict = analyticsAllowed();
    if (verdict === null) return;                   /* still unknown: keep waiting */
    resolvedOnce = true;
    clearDropTimer();
    gate.last_known = verdict ? 'allowed' : 'denied';
    var pending = queue.splice(0, queue.length);
    for (var i = 0; i < pending.length; i++) {
      if (verdict) { gate.allowed_sends += 1; runOne(pending[i]); }
      else { gate.blocked_sends += 1; }
    }
  }

  /* The public door. `fn` is only ever called when analytics processing is allowed. */
  gate.send = function (fn) {
    if (typeof fn !== 'function') return;
    var verdict = analyticsAllowed();
    if (verdict === true) { gate.last_known = 'allowed'; gate.allowed_sends += 1; runOne(fn); return; }
    if (verdict === false) { gate.last_known = 'denied'; gate.blocked_sends += 1; return; }
    /* The answer will never come: refuse now rather than hold something forever. */
    if (unresolvable) { gate.blocked_sends += 1; gate.dropped_unresolved += 1; return; }
    /* Unknown: hold it, bounded, and let a consent decision release or drop it. */
    if (queue.length < MAX_QUEUE) { queue.push(fn); gate.queued += 1; }
    else { gate.dropped_unresolved += 1; }
  };

  gate.state = function () {
    var cp = api();
    var read = function (fn) {
      try { return cp && typeof cp[fn] === 'function' ? cp[fn]() : null; } catch (e) { return null; }
    };
    return {
      api_loaded: gate.api_loaded,
      analytics_allowed: analyticsAllowed(),
      marketing_allowed: read('marketingAllowed'),
      sale_of_data_allowed: read('saleOfDataAllowed'),
      should_show_banner: read('shouldShowBanner'),
      sale_of_data_region: read('saleOfDataRegion'),
      consent: read('currentVisitorConsent'),
      last_known: gate.last_known,
      queued: queue.length
    };
  };

  /* A permission change must activate allowed processing without polling. Shopify
     dispatches visitorConsentCollected on document when consent is collected. */
  try {
    document.addEventListener('visitorConsentCollected', function () {
      gate.consent_events += 1;
      flush();
    });
  } catch (e) { /* no listener support is not a reason to send */ }

  /* Load the API. Until this resolves, analyticsAllowed() is null and nothing sends. */
  try {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (error) {
          if (error) {
            gate.load_error = (error && error.message) || String(error);
          } else {
            gate.api_loaded = true;
          }
          flush();
        }
      );
    } else {
      gate.load_error = 'loadFeatures_unavailable';
      unresolvable = true;
      gate.last_known = 'unresolvable';
    }
  } catch (e) {
    gate.load_error = (e && e.message) || 'loadFeatures_threw';
    unresolvable = true;
    gate.last_known = 'unresolvable';
  }

  /* If the API was asked but never answers, the queue is DROPPED rather than flushed.
     Failing in the permissive direction is the defect this gate exists to prevent.
     Not armed when we already know the answer can never come -- send() refuses those
     outright, so there is nothing for a timer to do. */
  if (!unresolvable) {
    try {
      dropTimer = window.setTimeout(function () {
        dropTimer = null;
        if (resolvedOnce) return;
        if (analyticsAllowed() !== null) { flush(); return; }
        gate.dropped_unresolved += queue.length;
        queue.length = 0;
        gate.last_known = 'unresolved_dropped';
        unresolvable = true;
      }, LOAD_TIMEOUT_MS);
    } catch (e) { /* no timer support: send() simply keeps queueing, bounded */ }
  }
})();

(function () {
  'use strict';

  /* Install once. A theme can include a script twice -- via a section and a layout,
     or after a section re-render -- and a second wrapper would double every event and
     silently double that product's demand. */
  if (window.__bbCartDemandInstalled) return;
  window.__bbCartDemandInstalled = true;

  var ENDPOINT = '/apps/bb-wishlist/events/cart-add';
  var MAX_EVENTS = 20;
  var DEBUG_RING = 5;

  /* BOUNDED, SILENT OBSERVABILITY.

     Commissioning could not see this collector work. The harness wrapped window.fetch
     ABOVE it, while telemetry is sent through the ORIGINAL reference below it -- by
     design, so the collector cannot intercept itself. Telemetry therefore never passes
     the observer, and "0 events seen" was structurally guaranteed whether or not the
     collector was working. It was.

     So the collector reports on itself instead of being spied on from above. Capped at
     five entries, no console output, no network cost: an unbounded log on a storefront
     is a memory leak, and chatty logging in a customer's console is a support ticket. */
  var debug = {
    installed_at: new Date().toISOString(),
    cart_requests_seen: 0,
    items_derived: 0,
    telemetry_attempts: 0,
    telemetry_ok: 0,
    telemetry_failed: 0,
    last_error: null,
    recent: []
  };
  window.__bbCartDemand = debug;

  function note(entry) {
    debug.recent.push(entry);
    if (debug.recent.length > DEBUG_RING) debug.recent.shift();
    return entry;
  }

  /* Keep the ORIGINAL, bound to window. Two reasons, both load-bearing:
     1. our own telemetry POST must use it, or the wrapper intercepts itself; and
     2. another script may wrap fetch after us, and we must not un-wrap them. */
  var nativeFetch = window.fetch.bind(window);

  /* ── is this a Shopify cart-add request? ──────────────────────────────── */

  function requestUrl(input) {
    try {
      if (typeof input === 'string') return input;
      if (input instanceof URL) return input.href;
      /* A Request object. Reading .url does NOT consume the body -- only .json(),
         .text() and friends do, and we never call those on the original. */
      if (input && typeof input.url === 'string') return input.url;
    } catch (e) { /* a malformed input is not our business to fix */ }
    return '';
  }

  function isCartAddUrl(url) {
    if (!url) return false;
    try {
      /* Locale-prefixed storefronts serve /fr-ca/cart/add.js, and Shopify.routes.root
         carries that prefix. Matching the path segment covers every root without
         hard-coding a locale list. Query strings are stripped first so ?foo=/cart/add
         cannot be mistaken for the real thing. */
      var path = String(url).split('?')[0].split('#')[0];
      return /\/cart\/add(\.js|\.json)?$/.test(path);
    } catch (e) { return false; }
  }

  /* ── what did Shopify actually add? ───────────────────────────────────── */

  /* Derived from the SUCCESS RESPONSE, never from the request. A form can ask for 5
     of something with 2 in stock; only the response knows what really happened.
     Shopify returns either a single line item or { items: [...] } for a multi-add. */
  function itemsFromResponse(body) {
    if (!body || typeof body !== 'object') return [];
    var raw = Array.isArray(body.items) ? body.items
      : (body.product_id || body.id ? [body] : []);
    var out = [];
    for (var i = 0; i < raw.length && out.length < MAX_EVENTS; i++) {
      var it = raw[i];
      if (!it || typeof it !== 'object') continue;
      var productId = it.product_id;
      var quantity = it.quantity;
      if (productId === null || productId === undefined) continue;
      /* Number(null) === 0. Reject the empties before converting, or a null quantity
         becomes a cart add worth nothing. */
      if (quantity === null || quantity === undefined || quantity === '') continue;
      var q = Number(quantity);
      if (!isFinite(q) || q < 1) continue;
      out.push({
        product_id: String(productId),
        variant_id: (it.variant_id !== null && it.variant_id !== undefined)
          ? String(it.variant_id) : (it.id !== null && it.id !== undefined ? String(it.id) : null),
        quantity: Math.min(100, Math.round(q))
      });
    }
    return out;
  }

  /* ── where did it come from? ──────────────────────────────────────────── */

  /* Attribution is useful analytics and is NOT required by the 0.12 priority signal,
     so this reports 'unknown' rather than guessing. A wrong surface is worse than an
     honest absent one. */
  function detectSurface() {
    try {
      var p = window.location.pathname || '';
      if (document.querySelector('quick-add-modal[open], .quick-add-modal.is-open')) return 'quick_add';
      if (/\/products\//.test(p)) return 'product_page';
      if (/\/collections\//.test(p)) return 'collection_grid';
      if (/\/search/.test(p)) return 'search';
      if (/\/cart/.test(p)) return 'cart_cross_sell';
      return 'unknown';
    } catch (e) { return 'unknown'; }
  }

  function eventId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID().replace(/-/g, '');
      }
    } catch (e) { /* fall through */ }
    return 'e' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
  }

  /* ── delivery ─────────────────────────────────────────────────────────── */

  function send(entry, events) {
    if (!events.length) return;
    var payload = JSON.stringify({ events: events });
    debug.telemetry_attempts += 1;
    entry.telemetry_attempted = true;

    /* CONSENT GATE. The body is already built; whether it leaves the browser is the
       gate's decision. Held (not sent) while the consent state is unknown, dropped if
       analytics processing is denied, released if a later consent decision allows it.
       The gate never throws outward. */
    var gate = window.__bbConsentGate;
    if (!gate || typeof gate.send !== 'function') {
      /* The gate is in this same file, so this is close to impossible -- but the
         answer to "I cannot tell whether this is permitted" is still no. */
      entry.telemetry_status = 'consent_gate_missing';
      debug.telemetry_blocked = (debug.telemetry_blocked || 0) + 1;
      return;
    }
    gate.send(function () { doSend(entry, payload); });
  }

  function doSend(entry, payload) {
    /* keepalive so the POST survives the navigation a cart add often triggers.
       nativeFetch, so this request cannot be intercepted by our own wrapper. */
    try {
      nativeFetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
        credentials: 'same-origin'
      }).then(function (res) {
        entry.telemetry_status = res && res.status !== undefined ? res.status : null;
        debug.telemetry_ok += 1;
      })['catch'](function (err) {
        /* Recorded, never logged. A blocked request (an extension, a security suite)
           throws a TypeError here with no status, which is exactly how it is told apart
           from a request that reached Express and was refused. There is no retry: a
           dropped analytics event is a rounding error, and a retry loop firing during
           checkout is a real problem. */
        debug.telemetry_failed += 1;
        entry.telemetry_status = 'network_error';
        entry.error = err && err.message ? err.message : 'send_failed';
        debug.last_error = entry.error;
      });
    } catch (e) {
      debug.telemetry_failed += 1;
      entry.error = e && e.message ? e.message : 'send_threw';
      debug.last_error = entry.error;
    }
  }

  /* ── the wrapper ──────────────────────────────────────────────────────── */

  window.fetch = function (input, init) {
    var promise = nativeFetch(input, init);

    var url;
    try { url = requestUrl(input); } catch (e) { url = ''; }
    if (!isCartAddUrl(url)) return promise;

    debug.cart_requests_seen += 1;
    var entry = note({ url: url, at: new Date().toISOString(), status: null, ok: null,
                       shape: null, items_derived: null, telemetry_status: null, error: null });

    /* THE CONTRACT WITH THE CALLER: return the original promise, untouched. The theme
       gets the same response object it would have got, unread, at the same time. The
       observation below hangs off a separate branch that nothing awaits, so an
       analytics failure cannot become a cart failure and cannot add a millisecond to
       the add-to-cart path. */
    promise.then(function (response) {
      try {
        /* A CLONE. Reading the original would consume the body the theme is about to
           parse, and the add-to-cart would break in a way that looks like Shopify's
           fault. */
        entry.status = response && response.status !== undefined ? response.status : null;
        entry.ok = !!(response && response.ok);
        if (!response || !response.ok || typeof response.clone !== 'function') {
          entry.error = 'response_not_ok';
          return;
        }
        response.clone().json().then(function (body) {
          try {
            /* Which of Shopify's two legitimate success shapes did we actually get?
               Recorded rather than assumed: the tests' fixture is not evidence about
               the real PDP response. */
            entry.shape = !body ? 'empty'
              : (Array.isArray(body.items) ? 'items_array'
                : (body.status ? 'rejected_with_status' : 'single_line_item'));

            /* Shopify signals a rejected add with a `status` field in a 200 body --
               sold out, invalid variant. Those are not demand. */
            if (body && body.status) { entry.items_derived = 0; return; }
            var items = itemsFromResponse(body);
            entry.items_derived = items.length;
            debug.items_derived += items.length;
            if (!items.length) { entry.error = 'no_items_derived'; return; }

            var now = new Date().toISOString();
            var surface = detectSurface();
            send(entry, items.map(function (it) {
              return {
                event_type: 'cart_add',
                event_id: eventId(),        // one per LINE ITEM, so a 2-item add is 2 events
                product_id: it.product_id,
                variant_id: it.variant_id,
                quantity: it.quantity,
                occurred_at: now,
                source_surface: surface
              };
            }));
          } catch (e) {
            entry.error = 'parse_failed';
            debug.last_error = e && e.message ? e.message : String(e);
          }
        })['catch'](function () { /* an unparseable body is not an error worth surfacing */ });
      } catch (e) { /* nor is anything else in here */ }
    })['catch'](function () {
      /* The cart request itself failed. That is the theme's to handle and ours to
         ignore -- a failed add is not demand. Swallowing it here also stops this
         branch becoming an unhandled rejection in the shopper's console. */
    });

    return promise;
  };
})();

/* ===========================================================================
   PRODUCT VIEW DEMAND -- a SEPARATE collector sharing this one asset.

   NAMING DEBT, recorded deliberately: this file is called bb-cart-demand.js and now
   carries more than cart demand. Renaming it would mean a live-theme change for no
   functional gain, so it stays. If a third storefront signal joins, extract to
   bb-commerce-signals.js in a controlled sprint.

   Cart Add Demand is LIVE ACCEPTED. Nothing below touches cart state, the cart fetch
   wrapper, or the cart debug object. The two collectors share an asset and nothing
   else, so neither can break the other.

   WHAT COUNTS: a credible PDP view. Not impressions, not quick view. The audit found
   fourteen product template paths -- two layout-none AJAX fragments, the
   ?view=quick_add modal and a wishlist card renderer all render on the product route
   without anyone viewing a product. Hence a PDP-only server marker plus client-side
   evidence, rather than counting at Liquid render time.
   =========================================================================== */
(function () {
  'use strict';

  if (window.__bbViewDemandInstalled) return;
  window.__bbViewDemandInstalled = true;

  var VIEW_ENDPOINT = '/apps/bb-wishlist/events/product-view';
  var DWELL_MS = 2000;
  var SUPPRESS_MS = 30000;

  /* Its OWN debug object. Sharing the cart's would let one collector's counters be
     corrupted by the other's bugs. */
  var vdebug = {
    installed_at: new Date().toISOString(),
    pdp_detected: false, product_id: null,
    emitted: 0, suppressed_recent: 0, skipped_reason: null,
    visible_ms: 0, telemetry_status: null, last_error: null
  };
  window.__bbViewDemand = vdebug;

  /* Its own native reference, taken the same way and for the same reason: telemetry
     must not pass through any wrapper, including the cart one. */
  var viewNativeFetch = window.fetch.bind(window);

  /* -- is this a REAL product detail page? -------------------------------- */

  var host = null;
  try { host = document.querySelector('[data-bb-pdp-view][data-product-id]'); } catch (e) { host = null; }
  if (!host) { vdebug.skipped_reason = 'no_pdp_marker'; return; }

  var productId = host.getAttribute('data-product-id');
  if (!productId || !/^[0-9]+$/.test(productId)) { vdebug.skipped_reason = 'no_product_id'; return; }
  vdebug.pdp_detected = true;
  vdebug.product_id = productId;

  /* -- things that are not customer attention ----------------------------- */

  try {
    if (window.Shopify && window.Shopify.designMode) { vdebug.skipped_reason = 'design_mode'; return; }
    if (window.location.search.indexOf('preview_theme_id') !== -1) { vdebug.skipped_reason = 'theme_preview'; return; }
    /* A prerendered document is the browser guessing, not a person looking. */
    if (document.prerendering) { vdebug.skipped_reason = 'prerendering'; return; }
  } catch (e) { /* a hostile environment is not a reason to throw on a product page */ }

  /* Rapid reload / back-button noise. sessionStorage is per-tab, dies with the tab,
     is never transmitted, and identifies nobody -- a local suppression cache, not a
     visitor id. */
  var suppressKey = 'bbpv:' + productId;
  try {
    var prev = window.sessionStorage.getItem(suppressKey);
    if (prev && (Date.now() - Number(prev)) < SUPPRESS_MS) {
      vdebug.suppressed_recent += 1;
      vdebug.skipped_reason = 'suppressed_recent';
      return;
    }
  } catch (e) { /* private mode: proceed rather than lose the signal entirely */ }

  /* -- credible attention: VISIBLE dwell, or a real interaction ------------ */

  var emitted = false;
  var visibleMs = 0;
  var lastTick = null;
  var timer = null;

  function stopTimer() {
    if (timer) { clearInterval(timer); timer = null; }
    lastTick = null;
  }

  function viewEventId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID().replace(/-/g, '');
      }
    } catch (e) { /* fall through */ }
    return 'v' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
  }

  var INTERACTIONS = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'];

  function detach() {
    document.removeEventListener('visibilitychange', onVisibility);
    for (var d = 0; d < INTERACTIONS.length; d++) {
      window.removeEventListener(INTERACTIONS[d], onInteract, true);
    }
  }

  function emit() {
    if (emitted) return;
    emitted = true;
    stopTimer();
    detach();

    try { window.sessionStorage.setItem(suppressKey, String(Date.now())); } catch (e) { /* fine */ }

    var ev = {
      event_type: 'product_view',
      event_id: viewEventId(),
      product_id: String(productId),
      occurred_at: new Date().toISOString(),
      source_surface: 'product_page'
    };
    vdebug.emitted += 1;
    vdebug.visible_ms = Math.round(visibleMs);

    /* CONSENT GATE. `emitted` and the suppression key are already set above, on
       purpose: credible attention HAS been observed, and that determination is
       independent of whether we are permitted to report it. Setting them here too
       means a denied view is not re-evaluated on every scroll tick. */
    var vgate = window.__bbConsentGate;
    if (!vgate || typeof vgate.send !== 'function') {
      vdebug.telemetry_status = 'consent_gate_missing';
      return;
    }
    vgate.send(function () { viewDoSend(ev); });
  }

  function viewDoSend(ev) {
    try {
      viewNativeFetch(VIEW_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: [ev] }),
        keepalive: true,
        credentials: 'same-origin'
      }).then(function (res) {
        vdebug.telemetry_status = res && res.status !== undefined ? res.status : null;
      })['catch'](function (err) {
        /* A blocked request rejects with no status, which is how it is told apart from
           one that reached Express and was refused. No retry: a dropped analytics
           event is a rounding error. */
        vdebug.telemetry_status = 'network_error';
        vdebug.last_error = err && err.message ? err.message : 'send_failed';
      });
    } catch (e) {
      vdebug.last_error = e && e.message ? e.message : 'send_threw';
    }
  }

  /* VISIBLE dwell, not wall clock. A background tab must not become product demand
     merely because two seconds elapsed somewhere the shopper cannot see. */
  function tick() {
    if (document.visibilityState !== 'visible') { lastTick = null; return; }
    var now = Date.now();
    if (lastTick !== null) visibleMs += now - lastTick;
    lastTick = now;
    if (visibleMs >= DWELL_MS) emit();
  }

  function onVisibility() {
    if (document.visibilityState === 'visible') { lastTick = Date.now(); }
    else { lastTick = null; }
  }

  /* A real interaction is credible attention immediately -- but only a TRUSTED one.
     A synthetic event dispatched by a script is not a customer. */
  function onInteract(e) {
    if (e && e.isTrusted === false) return;
    emit();
  }

  document.addEventListener('visibilitychange', onVisibility);
  for (var i = 0; i < INTERACTIONS.length; i++) {
    window.addEventListener(INTERACTIONS[i], onInteract, { capture: true, passive: true });
  }

  if (document.visibilityState === 'visible') lastTick = Date.now();
  timer = setInterval(tick, 250);

  /* ONE event per document. Nothing here listens to URL changes: product-info.js and
     facets.js both use pushState/replaceState for variant and filter updates, and a
     URL watcher would count one PDP many times over. */
})();
